// ============================================================================
// AUTH CONTEXT - Global Authentication State Management
// ============================================================================

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import {
  login as authServiceLogin,
  loginWithEntra as authServiceLoginWithEntra,
  logout as authServiceLogout,
  getIdToken,
  getCurrentAuthenticatedUser,
  fetchUserProfile,
  AccountPendingApprovalError,
  UserProfile
} from '../services/authService';
import { setAccessToken as setApiAccessToken, setAuthFunctions, api } from '../services/api';

interface AuthContextType {
  user: UserProfile | null;
  loading: boolean;
  authError: string | null;
  isAuthenticated: boolean;
  accessToken: string | null;
  login: () => Promise<void>;
  loginWithEntra: () => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  refreshUser: () => Promise<void>;
  // Holds a global Cedar action (from /me). Display only: the backend checks every call.
  can: (action: string) => boolean;
  canAny: (...actions: string[]) => boolean;
  // Signed in but limited to the guest ceiling (no Workspace::Read).
  isGuestOnly: boolean;
  updateNavPreferences: (pinnedItems: string[]) => Promise<void>;
}

type AuthCheckResult = 'success' | 'unauthenticated' | 'retry';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  /**
   * Attempt a single auth check cycle.
   */
  const attemptAuthCheck = async (): Promise<AuthCheckResult> => {
    const currentUser = await getCurrentAuthenticatedUser();
    if (!currentUser) {
      setUser(null);
      setAccessToken(null);
      return 'unauthenticated';
    }

    const token = await getIdToken();
    if (!token) {
      console.error('No ID token available');
      return 'retry';
    }

    setAccessToken(token);
    let profile: UserProfile | null;
    try {
      profile = await fetchUserProfile(token);
    } catch (error) {
      if (error instanceof AccountPendingApprovalError) {
        // Not a transient failure — retrying three times would only delay the
        // explanation the user is owed.
        setAuthError(error.message);
        setUser(null);
        return 'unauthenticated';
      }
      throw error;
    }
    if (profile) {
      setUser(profile);
      return 'success';
    }

    console.error('Failed to fetch user profile from backend');
    setUser(null);
    return 'retry';
  };

  /**
   * Check current authentication status with retry logic
   */
  const checkAuthStatus = async (retries = 3) => {
    setLoading(true);
    setAuthError(null);

    try {
      for (let attempt = 0; attempt < retries; attempt++) {
        try {
          const result = await attemptAuthCheck();
          if (result !== 'retry') return;
        } catch (error: any) {
          console.error(`Auth status check attempt ${attempt + 1}/${retries}:`, error.message);
          const isLastAttempt = attempt >= retries - 1;
          if (isLastAttempt) {
            setUser(null);
            setAccessToken(null);
            setAuthError('Authentication failed. Please try again.');
            return;
          }
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Initiate login flow
   */
  const rememberWhereTheyWere = () => {
    const currentPath = window.location.pathname;
    const authPages = ['/login', '/auth/callback', '/unauthorized'];
    if (!authPages.includes(currentPath)) {
      sessionStorage.setItem('auth_redirect', currentPath);
    }
  };

  const loginWithEntra = useCallback(async () => {
    try {
      rememberWhereTheyWere();
      await authServiceLoginWithEntra();
    } catch (error) {
      console.error('Microsoft login error:', error);
      throw error;
    }
  }, []);

  const login = useCallback(async () => {
    try {
      rememberWhereTheyWere();
      await authServiceLogin();
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }, []);

  /**
   * Handle logout
   */
  const handleLogout = useCallback(async () => {
    try {
      setUser(null);
      setAccessToken(null);
      await authServiceLogout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  }, []);

  /**
   * Refresh access token
   */
  const refreshToken = useCallback(async () => {
    try {
      const token = await getIdToken();
      if (token) {
        setAccessToken(token);

        // Also refresh user profile if needed
        if (!user) {
          const profile = await fetchUserProfile(token);
          if (profile) {
            setUser(profile);
          }
        }
      } else {
        // If token refresh fails, logout
        await handleLogout();
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      await handleLogout();
    }
  }, [user, handleLogout]);

  // Initialize auth state on mount
  useEffect(() => {
    const initAuth = async () => {
      // Check if we're on the callback page
      const isCallback = window.location.pathname === '/auth/callback';

      if (isCallback) {
        // Poll for auth readiness: check every 300ms up to 10 seconds
        const maxAttempts = 33; // ~10 seconds
        for (let i = 0; i < maxAttempts; i++) {
          try {
            const currentUser = await getCurrentAuthenticatedUser();
            if (currentUser) break;
          } catch {
            // Not ready yet
          }
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      } else {
        // Small delay for other pages
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      await checkAuthStatus();
      setInitialized(true);
    };

    initAuth();
  }, []);

  // Sync access token with API service whenever it changes
  useEffect(() => {
    setApiAccessToken(accessToken);
  }, [accessToken]);

  // Check auth status periodically after initialization (every 5 minutes)
  useEffect(() => {
    if (!initialized) return;

    const interval = setInterval(() => {
      if (accessToken) {
        refreshToken();
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [initialized, accessToken, refreshToken]);

  // Auto-refresh token before expiration
  useEffect(() => {
    if (!accessToken) return;

    try {
      // Decode JWT to get expiration
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      const expiresAt = payload.exp * 1000;
      const refreshAt = expiresAt - (5 * 60 * 1000); // 5 min before expiration
      const timeout = refreshAt - Date.now();

      if (timeout > 0) {
        const timer = setTimeout(() => {
          refreshToken();
        }, timeout);
        return () => clearTimeout(timer);
      }
    } catch (error) {
      console.error('Error parsing token:', error);
    }
  }, [accessToken, refreshToken]);

  /**
   * Refresh user profile from backend
   */
  const refreshUser = useCallback(async () => {
    try {
      if (accessToken) {
        const profile = await fetchUserProfile(accessToken);
        if (profile) {
          setUser(profile);
        }
      }
    } catch (error) {
      console.error('User profile refresh error:', error);
    }
  }, [accessToken]);

  const permissions = useMemo(() => new Set(user?.permissions ?? []), [user]);
  const can = useCallback((action: string): boolean => permissions.has(action), [permissions]);
  const canAny = useCallback((...actions: string[]): boolean => actions.some((a) => permissions.has(a)), [permissions]);
  const isGuestOnly = !!user && !permissions.has('Workspace::Read');

  const updateNavPreferences = useCallback(async (pinnedItems: string[]): Promise<void> => {
    const previous = user?.navPreferences;
    setUser(prev => prev ? { ...prev, navPreferences: { pinnedItems } } : prev);
    try {
      await api.patch('/api/users/me/preferences', { pinnedItems });
    } catch (error) {
      setUser(prev => prev ? { ...prev, navPreferences: previous } : prev);
      console.error('Failed to save nav preferences:', error);
    }
  }, [user]);

  // Setup auth functions in API service
  useEffect(() => {
    setAuthFunctions(refreshToken, handleLogout);
  }, [refreshToken, handleLogout]);

  const value = useMemo((): AuthContextType => ({
    user,
    loading,
    authError,
    isAuthenticated: !!user,
    accessToken,
    login,
    loginWithEntra,
    logout: handleLogout,
    refreshToken,
    refreshUser,
    can,
    canAny,
    isGuestOnly,
    updateNavPreferences,
  }), [user, loading, authError, accessToken, login, loginWithEntra, handleLogout, refreshToken, refreshUser, can, canAny, isGuestOnly, updateNavPreferences]);

  return (
    <AuthContext.Provider value={value}> {/* NOSONAR — value is useMemo-wrapped */}
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to access auth context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
